import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Zap, ArrowLeft, Image } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { useSubscription } from "../hooks/useSubscription";

const FREE_FEATURES = [
  "5 posts per day (7-day trial)",
  "All post frameworks & templates",
  "Research-backed content",
];

const PRO_FEATURES = [
  "Unlimited posts per day",
  "Carousel post creation",
  "All post frameworks & templates",
  "Research-backed content from Reddit, GitHub & more",
  "Priority support",
];

const ULTRA_FEATURES = [
  "Everything in Pro",
  "AI image generation for every post",
  "Infographic creation",
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { isPro, isUltra, isTrialActive, trialDaysRemaining } = useSubscription();
  const [loading, setLoading] = useState(null); // "pro" | "ultra" | null

  const handleSubscribe = async (plan) => {
    setLoading(plan);
    try {
      const res = await api.post("/billing/checkout", { plan });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 pt-8 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold mb-2">Simple, transparent pricing</h1>
          <p className="text-muted-foreground">
            {isTrialActive
              ? `You have ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? "s" : ""} left in your free trial.`
              : "Upgrade to keep creating great LinkedIn content."}
          </p>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {/* Free card */}
          <div className="rounded-xl border border-border bg-card p-6 flex flex-col">
            <div className="mb-4">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Free Trial
              </span>
              <div className="mt-1 text-3xl font-bold">$0</div>
              <p className="text-sm text-muted-foreground mt-1">7 days, no card required</p>
            </div>

            <ul className="space-y-2 flex-1 mb-6">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <div className="rounded-lg border border-border bg-muted px-4 py-2 text-center text-sm text-muted-foreground">
              {isTrialActive
                ? `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? "s" : ""} remaining`
                : "Trial ended"}
            </div>
          </div>

          {/* Pro card */}
          <div className="rounded-xl border-2 border-primary bg-card p-6 flex flex-col relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                RECOMMENDED
              </span>
            </div>

            <div className="mb-4">
              <span className="text-xs font-medium uppercase tracking-wide text-primary">
                Pro
              </span>
              <div className="mt-1 text-3xl font-bold">
                $19.99
                <span className="text-base font-normal text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Cancel anytime · Taxes handled by Lemon Squeezy
              </p>
            </div>

            <ul className="space-y-2 flex-1 mb-6">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            {isPro && !isUltra ? (
              <div className="rounded-lg bg-primary/10 text-primary px-4 py-2 text-center text-sm font-medium">
                ✓ Your current plan
              </div>
            ) : (
              <button
                onClick={() => handleSubscribe("pro")}
                disabled={loading !== null || isUltra}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Zap className="w-4 h-4" />
                {loading === "pro" ? "Redirecting…" : "Subscribe — $19.99/mo"}
              </button>
            )}
          </div>

          {/* Ultra card */}
          <div className="rounded-xl border-2 border-violet-500 bg-card p-6 flex flex-col relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-violet-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                MOST POWERFUL
              </span>
            </div>

            <div className="mb-4">
              <span className="text-xs font-medium uppercase tracking-wide text-violet-500">
                Ultra
              </span>
              <div className="mt-1 text-3xl font-bold">
                $49.99
                <span className="text-base font-normal text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Cancel anytime · Taxes handled by Lemon Squeezy
              </p>
            </div>

            <ul className="space-y-2 flex-1 mb-6">
              {ULTRA_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-violet-500 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            {isUltra ? (
              <div className="rounded-lg bg-violet-500/10 text-violet-500 px-4 py-2 text-center text-sm font-medium">
                ✓ Your current plan
              </div>
            ) : (
              <button
                onClick={() => handleSubscribe("ultra")}
                disabled={loading !== null}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-violet-500 text-white px-4 py-2.5 text-sm font-semibold hover:bg-violet-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Image className="w-4 h-4" />
                {loading === "ultra" ? "Redirecting…" : "Subscribe — $49.99/mo"}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          Payments securely processed by{" "}
          <a
            href="https://www.lemonsqueezy.com"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
          >
            Lemon Squeezy
          </a>
          . All taxes handled automatically.
        </p>
      </div>
    </div>
  );
}
